import React, { useContext, useEffect, useRef, useState } from 'react';
import { Modal } from '../Common/Modal';
import Alert from '../Common/Alert';
import { ElectronicSign } from '../Common/ElectronicSign';
import visitService from '../../services/visitService';
import { VISIT_TYPES } from '../../utils/constants';
import { visits as visitMessages, common as commonMessages } from '../../utils/validationMessages';
import { formatDateDDMMMYYYY } from '../../utils/dateFormat';
import { buildLetterFooterSignOff } from '../../utils/letterSignOffFormat';
import { AuthContext } from '../../context/AuthContext';
import VisitModalHeader from './VisitModalHeader';
import './GenerateVisitLetterModal.css';

const getBodyPlainText = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
};

const GenerateFollowUpLetterModal = ({ isOpen, onClose, visit, resolvedPiName, onGenerated, mode = 'edit' }) => {
  const { user: authUser } = useContext(AuthContext);
  const [editorContent, setEditorContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [letterBodyError, setLetterBodyError] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const bodyEditorRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [blockFormat, setBlockFormat] = useState('p');
  const [fontFamily, setFontFamily] = useState('Arial');
  const [showESignModal, setShowESignModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [toast, setToast] = useState(null);
  const eSignRemarksRef = useRef('');
  const saveAndContinueModeRef = useRef(false);
  const bodyContentInitializedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setHeaderText('');
      setEditorContent('');
      setFooterText('');
      setSaving(false);
      setShowPreview(false);
      setHistory([]);
      setHistoryIndex(-1);
      setBlockFormat('p');
      setFontFamily('Arial');
      setLetterBodyError('');
      bodyContentInitializedRef.current = false;
      return;
    }
    if (!visit?.scheduledVisitID && !visit?.scheduledVisitId) return;

    const visitId = visit.scheduledVisitID ?? visit.scheduledVisitId;
    let cancelled = false;

    const applyDefaultTemplate = (d) => {
      const piName = d?.piName ?? d?.PIName ?? '';
      const projectDescription = d?.projectDescription ?? d?.ProjectDescription ?? '';
      const projectDescriptionLine = projectDescription ? `${projectDescription}\n` : '';
      const siteCode = d?.siteCode ?? d?.SiteCode ?? '';
      const siteAddress = d?.siteName ?? d?.SiteName ?? '';
      const craName = (d?.craName ?? d?.CRAName ?? '').toString().trim();
      const signOff = buildLetterFooterSignOff(authUser) || craName;
      const baseBodyText =
`Thank you for hosting the recent visit. Please find the follow-up details and any required next steps.`;
      const baseHeaderText =
`Date: ${formatDateDDMMMYYYY(new Date())}

To ${piName},
${projectDescriptionLine}${siteCode}
${siteAddress}

Subject: Follow-Up Letter for Site Visit.`;
      const baseFooterText =
`Thanks & Regards,
${signOff}`;
      const baseBodyHtml = baseBodyText.replace(/\n/g, '<br />');
      setHeaderText(baseHeaderText);
      setEditorContent(baseBodyHtml);
      setFooterText(baseFooterText);
      setHistory([baseBodyHtml]);
      setHistoryIndex(0);
    };

    visitService.getById(visitId).then((detail) => {
      if (cancelled) return;
      const savedBody = (detail.followUpLetterBody != null && String(detail.followUpLetterBody).trim() !== '') ? detail.followUpLetterBody : null;
      if (savedBody) {
        setHeaderText(detail.followUpLetterHeader ?? '');
        setEditorContent(detail.followUpLetterBody);
        setFooterText(detail.followUpLetterFooter ?? '');
        setHistory([detail.followUpLetterBody]);
        setHistoryIndex(0);
      } else {
        applyDefaultTemplate(detail);
      }
      setBlockFormat('p');
      setFontFamily('Arial');
      bodyContentInitializedRef.current = false;
    }).catch((err) => {
      if (!cancelled) {
        console.error('Failed to load visit for follow-up letter', err);
        applyDefaultTemplate(visit);
        setBlockFormat('p');
        setFontFamily('Arial');
        bodyContentInitializedRef.current = false;
      }
    });

    return () => { cancelled = true; };
  }, [isOpen, visit, authUser]);

  useEffect(() => {
    if (isOpen && visit && bodyEditorRef.current && !bodyContentInitializedRef.current) {
      bodyEditorRef.current.innerHTML = editorContent;
      if (editorContent) bodyContentInitializedRef.current = true;
    }
  }, [isOpen, visit, editorContent]);

  if (!visit) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Generate Follow-Up Letter">
        <p>No visit selected.</p>
      </Modal>
    );
  }

  const isViewMode = String(mode || 'edit').toLowerCase() === 'view';

  const doSendLetter = async (vRemarks, options = {}) => {
    const { closeAfter = true, isDraft = false } = options;
    await visitService.sendFollowUpLetter(visit.scheduledVisitID, {
      letterHeader: headerText,
      letterBody: editorContent,
      letterFooter: footerText,
      piEmail: null,
      vRemarks: vRemarks || undefined,
      isDraft,
    });
    if (onGenerated) onGenerated();
    setToast({ type: 'success', message: commonMessages.authSuccess });
    if (closeAfter) onClose();
  };

  const validateLetterBody = () => {
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setLetterBodyError(visitMessages.letterBodyEmpty);
      return false;
    }
    setLetterBodyError('');
    return true;
  };

  const handlePreview = () => {
    setLetterBodyError('');
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setLetterBodyError(visitMessages.letterPreviewEmpty);
      return;
    }
    setShowPreview(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!visit.scheduledVisitID) return;
    setLetterBodyError('');
    if (!validateLetterBody()) return;
    eSignRemarksRef.current = '';
    setShowESignModal(true);
  };

  const runAfterESign = () => {
    const vRemarks = eSignRemarksRef.current || '';
    const isSaveAndContinue = saveAndContinueModeRef.current;
    saveAndContinueModeRef.current = false;
    setShowESignModal(false);
    setSaving(true);
    doSendLetter(vRemarks, { closeAfter: !isSaveAndContinue, isDraft: isSaveAndContinue })
      .catch((err) => {
        console.error('Error generating follow-up letter:', err);
        const msg = err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Unable to create or update follow-up letter. Please try again.';
        setToast({ type: 'error', message: msg });
      })
      .finally(() => setSaving(false));
  };

  const handleSaveAndContinue = (e) => {
    e.preventDefault();
    if (!visit.scheduledVisitID) return;
    setLetterBodyError('');
    const text = getBodyPlainText(editorContent);
    if (!text) {
      setLetterBodyError(visitMessages.noDataToSave);
      return;
    }
    setSaving(true);
    doSendLetter('', { isDraft: true, closeAfter: true })
      .catch((err) => {
        console.error('Error saving follow-up letter draft:', err);
        const msg = err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Unable to save follow-up letter draft. Please try again.';
        setToast({ type: 'error', message: msg });
      })
      .finally(() => setSaving(false));
  };

  const handleClear = () => {
    setLetterBodyError('');
    bodyContentInitializedRef.current = false;
    setEditorContent('');
    setHistory(['']);
    setHistoryIndex(0);
    if (bodyEditorRef.current) bodyEditorRef.current.innerHTML = '';
  };

  const visitTypeLabel = VISIT_TYPES[visit.visitType] || visit.visitType || '';

  const pushHistory = (content) => {
    setHistory(prev => {
      const trimmed = historyIndex >= 0 ? prev.slice(0, historyIndex + 1) : [];
      if (trimmed.length > 0 && trimmed[trimmed.length - 1] === content) return trimmed;
      const next = [...trimmed, content];
      setHistoryIndex(next.length - 1);
      return next;
    });
  };

  const applyCommand = (command, value = null) => {
    if (!bodyEditorRef.current) return;
    bodyEditorRef.current.focus();
    document.execCommand(command, false, value);
    const html = bodyEditorRef.current.innerHTML;
    setEditorContent(html);
    pushHistory(html);
  };

  const handleBodyInput = (e) => {
    const html = e.currentTarget.innerHTML;
    setEditorContent(html);
    setLetterBodyError('');
    pushHistory(html);
  };

  const handleUndoClick = () => {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    const html = history[newIndex];
    setEditorContent(html);
    if (bodyEditorRef.current) bodyEditorRef.current.innerHTML = html;
  };

  const handleRedoClick = () => {
    if (historyIndex === -1 || historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    const html = history[newIndex];
    setEditorContent(html);
    if (bodyEditorRef.current) bodyEditorRef.current.innerHTML = html;
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      title={isViewMode ? 'View Follow-Up Letter' : 'Generate Follow-Up Letter'}
      className="generate-letter-modal"
    >
      <form onSubmit={(e) => { if (isViewMode) { e.preventDefault(); return; } handleSubmit(e); }}>
        <VisitModalHeader
          projectCode={visit.projectCode}
          siteName={visit.siteName}
          siteCode={visit.siteCode ?? visit.SiteCode}
          piName={resolvedPiName ?? visit.piName ?? visit.PIName ?? visit.pIName}
        />

        <div className="form-group">
          <label className="form-label">Visit Type</label>
          <input type="text" className="form-input" value={visitTypeLabel} disabled />
        </div>

        <div className="form-group generate-letter-header-block">
          <textarea className="form-textarea generate-letter-template" rows="6" value={headerText} readOnly />
        </div>

        <div className="form-group">
          {!isViewMode && (
            <div className="richtext-toolbar">
              <button type="button" className="rt-btn" onClick={() => applyCommand('bold')}>B</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('italic')}>I</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('underline')}>U</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('insertUnorderedList')}>•</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('insertOrderedList')}>1.</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('justifyLeft')}>L</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('justifyCenter')}>C</button>
              <button type="button" className="rt-btn" onClick={() => applyCommand('justifyRight')}>R</button>
              <button type="button" className="rt-btn" onClick={handleUndoClick}>↺</button>
              <button type="button" className="rt-btn" onClick={handleRedoClick}>↻</button>
              <div className="rt-select-wrap">
                <span className="rt-select-label">{blockFormat === 'h1' ? 'Heading 1' : blockFormat === 'h2' ? 'Heading 2' : blockFormat === 'h3' ? 'Heading 3' : 'Normal'}</span>
                <select
                  className="rt-select"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) return;
                    setBlockFormat(value);
                    applyCommand('formatBlock', value);
                  }}
                  value={blockFormat}
                  aria-label="Block format"
                >
                  <option value="p">Normal</option>
                  <option value="h1">Heading 1</option>
                  <option value="h2">Heading 2</option>
                  <option value="h3">Heading 3</option>
                </select>
              </div>
              <div className="rt-select-wrap rt-select-wrap--font">
                <span className="rt-select-label">{fontFamily || 'Arial'}</span>
                <select
                  className="rt-select"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value) return;
                    applyCommand('fontName', value);
                    setFontFamily(value);
                  }}
                  value={fontFamily}
                  aria-label="Font family"
                >
                  <option value="">Font Family</option>
                  <option value="Arial">Arial</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Calibri">Calibri</option>
                  <option value="Georgia">Georgia</option>
                </select>
              </div>
            </div>
          )}

          <div
            ref={bodyEditorRef}
            className="form-textarea richtext-editor"
            suppressContentEditableWarning
            onInput={isViewMode ? undefined : handleBodyInput}
            contentEditable={!isViewMode}
          />
          {letterBodyError && (
            <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{letterBodyError}</div>
          )}
        </div>

        <div className="form-group generate-letter-footer-block">
          <textarea className="form-textarea generate-letter-template" rows="4" value={footerText} readOnly />
        </div>

        <div className="generate-letter-footer">
          <button type="button" className="btn btn-primary" onClick={handlePreview} disabled={saving}>Preview</button>
          {!isViewMode && (
            <>
              <button type="button" className="btn btn-primary" onClick={handleSaveAndContinue} disabled={saving}>
                {saving ? 'Saving...' : 'Draft Save'}
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Generating...' : 'Submit'}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={handleClear} disabled={saving}>Clear</button>
            </>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </form>
    </Modal>

    <Modal
      isOpen={showPreview}
      onClose={() => setShowPreview(false)}
      size="large"
      title="Generate Follow-Up Letter Preview"
      className="generate-letter-preview-modal"
      footer={
        <button type="button" className="btn btn-secondary" onClick={() => setShowPreview(false)}>Close</button>
      }
    >
      <div className="generate-letter-preview-content">
        {headerText && <pre className="generate-letter-preview-header">{headerText}</pre>}
        <div className="generate-letter-preview-body" dangerouslySetInnerHTML={{ __html: editorContent }} />
        {footerText && <pre className="generate-letter-preview-footer">{footerText}</pre>}
      </div>
    </Modal>

    {showESignModal && (
      <ElectronicSign
        show={true}
        onClose={() => { setShowESignModal(false); eSignRemarksRef.current = ''; }}
        onAuthSuccess={(vRemarks) => { eSignRemarksRef.current = vRemarks || ''; }}
        onSave={runAfterESign}
      />
    )}
    {toast && (
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
        <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      </div>
    )}
    </>
  );
};

export default GenerateFollowUpLetterModal;

